import { useState, useMemo, useEffect } from 'react';
import orbitMenuConfig, { orbitMenuPresets } from '../config/orbitMenuConfig';

// Global state to share config across all hook instances
let globalConfigOverrides = {};
const listeners = new Set();

const notifyListeners = () => {
  listeners.forEach(listener => listener());
};

/**
 * Hook to manage OrbitMenu configuration with live customization support
 * Allows runtime override of config values for Visual Customizer
 * Uses global state to sync across all components
 */
export const useOrbitMenuConfig = () => {
  const [updateCounter, forceUpdate] = useState(0);

  useEffect(() => {
    const listener = () => forceUpdate(prev => prev + 1);
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);

  // Merge base config with global overrides
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const config = useMemo(() => {
    const merged = deepMerge(orbitMenuConfig, globalConfigOverrides);
    console.log('📦 Config recomputed:', merged.visual.radius, 'Counter:', updateCounter);
    return merged;
  }, [updateCounter]);

  // Update specific config value
  const updateConfig = (path, value) => {
    const newOverrides = { ...globalConfigOverrides };
    setNestedValue(newOverrides, path, value);
    globalConfigOverrides = newOverrides;
    console.log(`🔄 Config updated: ${path} = ${value}`, `Listeners: ${listeners.size}`);
    notifyListeners();
  };

  // Apply preset
  const applyPreset = (presetName) => {
    const preset = orbitMenuPresets[presetName];
    if (preset) {
      globalConfigOverrides = deepMerge(globalConfigOverrides, preset);
      notifyListeners();
    }
  };

  // Reset to default
  const resetConfig = () => {
    globalConfigOverrides = {};
    notifyListeners();
  };

  // Export current config as JSON
  const exportConfig = () => {
    return JSON.stringify(config, null, 2);
  };

  return {
    config,
    updateConfig,
    applyPreset,
    resetConfig,
    exportConfig,
    configOverrides: globalConfigOverrides,
  };
};

// Helper: Deep merge objects
function deepMerge(target, source) {
  const output = { ...target };

  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          Object.assign(output, { [key]: source[key] });
        } else {
          output[key] = deepMerge(target[key], source[key]);
        }
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }

  return output;
}

// Helper: Check if value is object
function isObject(item) {
  return item && typeof item === 'object' && !Array.isArray(item);
}

// Helper: Set nested value by path string (e.g., "visual.radius")
function setNestedValue(obj, path, value) {
  const keys = path.split('.');
  const lastKey = keys.pop();
  const nested = keys.reduce((o, k) => {
    if (!o[k]) o[k] = {};
    return o[k];
  }, obj);
  nested[lastKey] = value;
}

export default useOrbitMenuConfig;
